import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import {
  AccountInfo,
  AppliedDiscountMeta,
  AppliedPayableRestriction,
  GetSessionSummaryResponse,
  ModifiedDocumentMeta,
  roundTo2Decimals,
  SessionDocumentInfo,
  updateCollection,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  PoaSessionReviewPaymentActions,
  PoiSessionPaymentDetailsActions,
  PoiSessionReviewPaymentActions,
  SessionActions,
} from '../actions';
import {
  HostedPaymentsReviewPaymentComponentSessionActions,
  HostedPaymentsSessionComponentActions,
  MakePaymentPaymentLinksApiActions,
  MakePaymentSessionApiActions,
  PoaPaymentDetailsPaymentLinksApiActions,
  PoaPaymentDetailsSessionApiActions,
  PoiIntlMakePaymentPaymentLinksApiActions,
  PoiIntlMakePaymentSessionApiActions,
  PoiIntlMakePaymentComponentActions,
  PoiMakePaymentComponentActions,
  PoiPadResultComponentSessionActions,
  PoiSelectPaymentMethodComponentActions,
  PoaPadResultComponentSessionActions,
  HostedPaymentsPadResultComponentSessionActions,
  PoiStripeResultIntlSessionActions,
  PoiIntSelectPaymentTypeSessionActions,
  PoiPaymentReceiptComponentActions,
  PoaPaymentReceiptComponentActions,
  PoiIntlPaymentReceiptComponentActions,
} from '../../component-actions';

export interface SessionState {
  sessionSummary?: GetSessionSummaryResponse;
  selectedDocuments?: SessionDocumentInfo[];
  modifiedDocumentBurns?: ModifiedDocumentMeta[];
  appliedDiscountMeta?: AppliedDiscountMeta[];
  payableRestrictionsHistory?: AppliedPayableRestriction[];
}

const initialState: SessionState = {};

// Soft/hard paid-in-full + 48h RECENT — keep row icon/tooltip after deselect
// when preview omits the doc from the next payload.
const DUPLICATE_PAYMENT_HINTS = [
  'ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_SOFT',
  'ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_HARD',
  'ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_RECENT',
] as const;

const hasDuplicatePaymentHint = (hints: string[] | undefined): boolean =>
  DUPLICATE_PAYMENT_HINTS.some((h) => hints?.includes(h));

function updateSelectedDocuments(
  selectedDocuments: SessionDocumentInfo[],
  updatedDocuments: SessionDocumentInfo[],
): SessionDocumentInfo[] {
  return updateCollection(
    selectedDocuments ?? [],
    updatedDocuments ?? [],
    (current, updated) => current.urn === updated.urn,
  );
}

export const sessionReducers = createReducer(
  initialState,
  on(
    SessionActions.GetSessionSummarySuccessAction,
    HostedPaymentsSessionComponentActions.loadSessionSummarySuccess,
    PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
    PoaPaymentReceiptComponentActions.loadSessionSummarySuccess,
    PoiIntlPaymentReceiptComponentActions.loadSessionSummarySuccess,
    (state, action): SessionState => {
      const documents: SessionDocumentInfo[] = [];
      const payment = action.payload.payment;
      action.payload.payables.forEach((payable) => {
        payable.documents.forEach((document) => {
          documents.push({
            ...document,
            isPayable: true,
            restrictionHints: [],
            originalPayableAmount: document.payableAmount,
            originalSessionPayableAmount: document.payableAmount,
            isCreditMemo:
              document.payableAmount < 0 ||
              document.originalBalance < 0 ||
              document.openBalance < 0,
            isFullInvoiceDispute:
              !!document.dispute &&
              document.dispute.allowEntireInvoiceDisputes &&
              document.dispute.allowPaymentOnFullInvoiceDispute &&
              document.openBalance > 0 &&
              document.payableAmount === 0 &&
              roundTo2Decimals(
                document.openBalance + document.dispute.disputeTotal,
              ) === 0,
            isSelected: payment
              ? payment.transactions.some((x) =>
                  x.payables.some((y) => y.urn === document.urn),
                )
              : true,
          });
        });
      });

      return {
        ...state,
        sessionSummary: action.payload,
        selectedDocuments: documents,
      };
    },
  ),
  on(
    SessionActions.UpdateModifiedDocumentsUrnListAction,
    (state, action): SessionState => ({
      ...state,
      modifiedDocumentBurns: (state.modifiedDocumentBurns ?? [])
        .filter((x) => x.urn !== action.payload.urn)
        .concat(action.payload),
    }),
  ),
  on(
    PoiSessionPaymentDetailsActions.updateModifiedDocumentsUrnListAction,
    (state, action): SessionState => ({
      ...state,
      modifiedDocumentBurns: action.updatedModifiedDocumentsUrnList,
    }),
  ),
  on(
    SessionActions.UpdateSelectedDocumentsComplete,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: action.payload.updatedSessionDocuments,
    }),
  ),
  // deprecated in favor of component action sources
  on(
    SessionActions.IntlZeroDollarPaymentCalculated,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: updateSelectedDocuments(
        state.selectedDocuments,
        action.payload?.updatedSessionDocuments ?? state.selectedDocuments,
      ),
    }),
  ),
  on(
    PoiMakePaymentComponentActions.creditPreApplied,
    PoiMakePaymentComponentActions.creditApplied,
    PoiSelectPaymentMethodComponentActions.creditApplied,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: state.selectedDocuments.map((currentDoc) => {
        const updatedDoc = action.payload?.updatedSessionDocuments?.find(
          (d) => d.urn === currentDoc.urn,
        );
        return updatedDoc
          ? {
              ...updatedDoc,
              // Preserve duplicatePaymentSoftBlockPending from current state
              duplicatePaymentSoftBlockPending:
                currentDoc.duplicatePaymentSoftBlockPending,
            }
          : currentDoc;
      }),
      modifiedDocumentBurns:
        action.payload?.modifiedDocumentBurns ?? state.modifiedDocumentBurns,
    }),
  ),
  on(
    PoiSelectPaymentMethodComponentActions.paymentAccountSelected,
    (state): SessionState => {
      const zeroDollarAdjustedUrns = new Set(
        (state.modifiedDocumentBurns ?? [])
          .filter((m) => m.source === 'zeroDollarAdjustment')
          .map((m) => m.urn),
      );
      if (zeroDollarAdjustedUrns.size === 0) {
        return state;
      }
      return {
        ...state,
        selectedDocuments: state.selectedDocuments.map((doc) =>
          zeroDollarAdjustedUrns.has(doc.urn)
            ? {
                ...doc,
                payableAmount:
                  doc.preAdjustmentPayableAmount ?? doc.originalPayableAmount,
              }
            : doc,
        ),
      };
    },
  ),
  on(
    SessionActions.UpdateSelectedSessionDocumentsActionSuccess,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: action.payload.updatedSelectedDocuments,
    }),
  ),
  on(
    PoiSessionPaymentDetailsActions.selectedDocumentsUpdated,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: action.updatedSelectedDocuments,
    }),
  ),
  on(
    SessionActions.UpdatePaymentSuccessAction,
    PoiSessionReviewPaymentActions.sessionPaymentUpdated,
    PoaSessionReviewPaymentActions.sessionPaymentUpdated,
    HostedPaymentsReviewPaymentComponentSessionActions.updateSessionPaymentSuccess,
    PoiIntSelectPaymentTypeSessionActions.updateSessionPaymentSuccess,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: {
        ...state.sessionSummary,
        payment: {
          ...action.response,
          transactions: action.response.transactions.map((t) => {
            return {
              ...t,
              subtotal: roundTo2Decimals(
                t.payables
                  .filter((p) => p.transactionAmount > 0)
                  .map((p) => p.transactionAmount)
                  .reduce((a, b) => a + b, 0),
              ),
              totalCredit: roundTo2Decimals(
                t.payables
                  .filter((p) => p.transactionAmount < 0)
                  .map((p) => p.transactionAmount)
                  .reduce((a, b) => a + b, 0),
              ),
            };
          }),
        },
      },
    }),
  ),
  // deprecated in favor of component action sources
  on(
    SessionActions.IntlUpdateAppliedDiscountAmountAction,
    (state, action): SessionState => {
      let { selectedDocuments } = state;
      let { appliedDiscountMeta } = state;
      const newAppliedDiscountMetaList: AppliedDiscountMeta[] = [];
      action.payload.forEach((meta: AppliedDiscountMeta) => {
        const updatedDocument: SessionDocumentInfo =
          state.selectedDocuments.find((x) => x.urn === meta.urn);
        const updatedDiscountMeta: AppliedDiscountMeta =
          state.appliedDiscountMeta?.find((x) => x.urn === meta.urn) ?? {
            ...meta,
          };
        updatedDiscountMeta.discountState = meta.discountState;

        updatedDocument.payableAmount = meta.discountedPayableAmount;
        updatedDocument.discountAmount = meta.discountAmount;
        updatedDocument.discountDate = meta.discountDate;
        updatedDocument.appliedDiscountAmount = meta.appliedDiscountAmount;
        selectedDocuments = state.selectedDocuments
          .filter((x) => x.urn !== meta.urn)
          .concat(updatedDocument);

        if (state.appliedDiscountMeta) {
          appliedDiscountMeta = state.appliedDiscountMeta
            ?.filter((x) => x.urn !== meta.urn)
            ?.concat(updatedDiscountMeta);
        } else {
          newAppliedDiscountMetaList.push(updatedDiscountMeta);
        }
      });

      return {
        ...state,
        selectedDocuments: [...(selectedDocuments ?? [])],
        appliedDiscountMeta: [
          ...(appliedDiscountMeta ?? newAppliedDiscountMetaList),
        ],
      };
    },
  ),
  on(
    PoiMakePaymentComponentActions.discountsUpdated,
    PoiSelectPaymentMethodComponentActions.discountsUpdated,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: updateSelectedDocuments(
        state.selectedDocuments,
        action.payload.selectedDocuments,
      ),
      appliedDiscountMeta: action.payload.appliedDiscountMetadata,
    }),
  ),
  on(
    PoiMakePaymentComponentActions.payableRestrictionsApplied,
    PoiIntlMakePaymentComponentActions.payableRestrictionsApplied,
    PoiSelectPaymentMethodComponentActions.payableRestrictionsApplied,
    (state, action): SessionState => {
      const newPayloadDocumentUrns = new Set(
        action.payload.map((r) => r.documentUrn),
      );

      // Deselected docs drop out of the next preview payload. Keep prior duplicate-
      // payment hints (soft/hard paid-in-full + 48h RECENT) so the row icon/tooltip
      // still renders; page banners key off selection separately.

      const preservedRestrictions = (
        state.payableRestrictionsHistory ?? []
      ).filter(
        (r) =>
          !newPayloadDocumentUrns.has(r.documentUrn) &&
          hasDuplicatePaymentHint(r.hints),
      );

      const preservedDuplicateHintUrns = new Set(
        preservedRestrictions.map((r) => r.documentUrn),
      );

      return {
        ...state,
        payableRestrictionsHistory: [
          ...action.payload,
          ...preservedRestrictions,
        ],
        selectedDocuments: state.selectedDocuments.map((d) => {
          if (preservedDuplicateHintUrns.has(d.urn)) {
            return d;
          }

          const restriction = action.payload.find(
            (r) => r.documentUrn === d.urn,
          );

          return {
            ...d,
            isPayable: !restriction?.isRejected,
            restrictionHints: restriction?.hints,
            // Only set when preview stamped a status — avoid isPaid: undefined noise
            // that breaks equality expectations on unrelated restriction updates.
            ...(restriction?.pastPaymentStatus != null
              ? { isPaid: restriction.pastPaymentStatus }
              : {}),
          };
        }),
      };
    },
  ),
  on(
    SessionActions.UpdateSessionPaymentTransactionStatusAction,
    PoiSessionReviewPaymentActions.sessionPaymentTransactionsUpdated,
    PoaSessionReviewPaymentActions.sessionPaymentTransactionsUpdated,
    HostedPaymentsReviewPaymentComponentSessionActions.sessionPaymentTransactionsUpdated,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: {
        ...state.sessionSummary,
        payment: {
          ...state.sessionSummary.payment,
          transactions: action.payload,
        },
      },
    }),
  ),
  on(
    SessionActions.SetPaymentProcessAction,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: {
        ...state.sessionSummary,
        paymentProcess: action.paymentProcess,
      },
    }),
  ),
  on(
    PoiSessionPaymentDetailsActions.sessionDocumentDataUpdated,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: action.selectedDocuments,
      modifiedDocumentBurns: action.modifiedDocumentBurns,
      appliedDiscountMeta: action.appliedDiscountMetadata,
    }),
  ),
  on(
    PoiMakePaymentComponentActions.documentSelectionChanged,
    PoiIntlMakePaymentComponentActions.documentSelectionChanged,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: state.selectedDocuments.map((doc) => ({
        ...doc,
        isSelected: action.event.selection.some((x) => x.urn === doc.urn),
      })),
    }),
  ),
  on(
    PoiIntlMakePaymentComponentActions.buyerAccountSelected,
    (state, action): SessionState => ({
      ...state,
      selectedDocuments: state.selectedDocuments.filter(
        (doc) => doc.buyerAccount === action.buyerAccount,
      ),
      sessionSummary: {
        ...state.sessionSummary,
        payables: state.sessionSummary.payables.filter(
          (payable) => payable.accountInfo.number === action.buyerAccount,
        ),
      },
    }),
  ),
  on(
    PoaPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
    HostedPaymentsPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
    PoiPadResultComponentSessionActions.loadSessionRedirectStateSuccess,
    PoiStripeResultIntlSessionActions.loadSessionRedirectStateSuccess,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: action.payload.redirectState.sessionSummary,
      appliedDiscountMeta: action.payload.redirectState.appliedDiscountMeta,
      modifiedDocumentBurns: action.payload.redirectState.modifiedDocumentBurns,
      selectedDocuments: action.payload.redirectState.selectedDocuments,
    }),
  ),
  /* istanbul ignore next */
  on(
    MakePaymentPaymentLinksApiActions.platformJwtObtained,
    PoaPaymentDetailsPaymentLinksApiActions.platformJwtObtained,
    PoiIntlMakePaymentPaymentLinksApiActions.platformJwtObtained,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: {
        ...state.sessionSummary,
        externalUserUrn: action.externalUserBurn ?? null,
      },
    }),
  ),
  /* istanbul ignore next */
  on(
    MakePaymentSessionApiActions.sessionOwnershipUpdated,
    PoaPaymentDetailsSessionApiActions.sessionOwnershipUpdated,
    PoiIntlMakePaymentSessionApiActions.sessionOwnershipUpdated,
    (state, action): SessionState => ({
      ...state,
      sessionSummary: {
        ...state.sessionSummary,
        userEmail: action.userEmail,
      },
    }),
  ),
  on(
    PoiMakePaymentComponentActions.applySoftBlockDocumentDeselection,
    SessionActions.applySoftBlockDocumentDeselection,
    (state): SessionState => ({
      ...state,
      selectedDocuments: state.selectedDocuments.map((d) =>
        d.duplicatePaymentSoftBlockPending
          ? { ...d, duplicatePaymentSoftBlockPending: false, isSelected: false }
          : d,
      ),
    }),
  ),
);

export const BtpaySessionStore =
  createFeatureSelector<SessionState>('btpay session');

export const sessionMap = (state: SessionState): GetSessionSummaryResponse =>
  state.sessionSummary;

export const sessionAccountInfoMap = (state: SessionState): AccountInfo =>
  state.sessionSummary?.payables[0]?.accountInfo;

export const SessionStore = createSelector(BtpaySessionStore, sessionMap);

export const SessionAccountInfoStore = createSelector(
  BtpaySessionStore,
  sessionAccountInfoMap,
);

export const SelectedModifiedDocumentBurnsStore = createSelector(
  BtpaySessionStore,
  (state) => state.modifiedDocumentBurns,
);

export const SelectedAppliedDiscountMetaStore = createSelector(
  BtpaySessionStore,
  (state) => state.appliedDiscountMeta,
);
